import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCheck,
  faGripVertical,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Button } from "../ui/button/Button";

/**
 * Generic item shape for the dual-panel selector.
 * Both ScreenerColumn and StatisticField can be mapped to this.
 */
export interface SelectableItem {
  id: string;
  name: string;
  description?: string;
  group?: string;
}

export interface DualPanelItemSelectorProps {
  /** All available items to choose from */
  items: SelectableItem[];
  /** Currently selected item IDs (ordered) */
  selectedIds: string[];
  /** Called on every toggle or reorder with the new ordered selection */
  onSelectionChange: (ids: string[]) => void;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Title for the available (left) panel */
  availableTitle?: string;
  /** Title for the selected (right) panel */
  selectedTitle?: string;
  /** Message shown when no items are selected */
  emptyMessage?: string;
}

/**
 * A reusable two-panel selector with search, grouping, Select All / Clear All,
 * and drag-to-reorder in the selected panel. This is a controlled, presentational
 * component with no Dialog wrapper -- the parent is responsible for placing it
 * inside a Dialog or inline.
 */
export function DualPanelItemSelector({
  items,
  selectedIds,
  onSelectionChange,
  searchPlaceholder = "Search...",
  availableTitle = "Available Columns",
  selectedTitle = "Selected Columns",
  emptyMessage = "No columns selected",
}: DualPanelItemSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Deduplicate items by id and group them
  const groupedItems = useMemo(() => {
    const seenIds = new Set<string>();
    const groups: Record<string, SelectableItem[]> = {};

    for (const item of items) {
      if (seenIds.has(item.id)) continue;
      seenIds.add(item.id);
      const group = item.group || "Other";
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
    }

    return groups;
  }, [items]);

  // Filter items based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groupedItems;

    const q = searchTerm.trim().toLowerCase();
    const result: Record<string, SelectableItem[]> = {};

    for (const [group, groupItems] of Object.entries(groupedItems)) {
      const filtered = groupItems.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q),
      );
      if (filtered.length > 0) {
        result[group] = filtered;
      }
    }

    return result;
  }, [groupedItems, searchTerm]);

  // Toggle an item on or off
  const handleToggle = (itemId: string) => {
    if (selectedIds.includes(itemId)) {
      onSelectionChange(selectedIds.filter((id) => id !== itemId));
    } else {
      onSelectionChange([...selectedIds, itemId]);
    }
  };

  // Select all visible items (preserving existing order, appending new ones)
  const handleSelectAll = () => {
    const allIds = items.map((item) => item.id);
    // Deduplicate: keep existing order for already-selected, append new
    const merged = [...selectedIds];
    for (const id of allIds) {
      if (!merged.includes(id)) {
        merged.push(id);
      }
    }
    onSelectionChange(merged);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  // Drag handlers for reordering in the selected panel
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    const dragIndex = selectedIds.indexOf(draggedItem);
    if (dragIndex === -1) return;

    const newIds = [...selectedIds];
    newIds.splice(dragIndex, 1);
    newIds.splice(dropIndex, 0, draggedItem);

    onSelectionChange(newIds);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Resolve selected IDs to full item objects (preserving order)
  const orderedSelectedItems = useMemo(
    () =>
      selectedIds
        .map((id) => items.find((item) => item.id === id))
        .filter(Boolean) as SelectableItem[],
    [selectedIds, items],
  );

  return (
    <div className="space-y-4" data-testid="dual-panel-item-selector">
      {/* Search input */}
      <div>
        <input
          type="text"
          placeholder={searchPlaceholder}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="dual-panel-search"
        />
      </div>

      {/* Two-panel grid */}
      <div
        className="grid grid-cols-2 gap-4"
        style={{ minHeight: "400px" }}
      >
        {/* Available items (left panel) */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {availableTitle}
            </h3>
            <Button onClick={handleSelectAll} plain data-testid="select-all-btn">
              Select All
            </Button>
          </div>
          <div className="overflow-y-auto max-h-96 space-y-1">
            {Object.entries(filteredGroups).map(([group, groupItems]) => (
              <div key={group} className="mb-4">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">
                  {group}
                </h4>
                <div className="space-y-1">
                  {groupItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                      onClick={() => handleToggle(item.id)}
                      data-testid={`available-item-${item.id}`}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.description}
                          </div>
                        )}
                      </div>
                      <div
                        className={`w-5 h-5 flex items-center justify-center rounded border ${
                          selectedIds.includes(item.id)
                            ? "bg-indigo-600 border-indigo-600"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {selectedIds.includes(item.id) && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-3 w-3 text-white"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected items (right panel) */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {selectedTitle} ({selectedIds.length})
            </h3>
            <Button onClick={handleDeselectAll} plain data-testid="clear-all-btn">
              Clear All
            </Button>
          </div>
          <div className="overflow-y-auto max-h-96">
            {orderedSelectedItems.length === 0 ? (
              <p
                className="text-sm text-gray-500 dark:text-gray-400 text-center py-8"
                data-testid="empty-selection-message"
              >
                {emptyMessage}
              </p>
            ) : (
              <div className="space-y-1">
                {orderedSelectedItems.map((item, index) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`flex items-center p-2 rounded cursor-move transition-all ${
                      draggedItem === item.id ? "opacity-50" : ""
                    } ${
                      dragOverIndex === index
                        ? "border-t-2 border-indigo-500"
                        : ""
                    } hover:bg-gray-100 dark:hover:bg-gray-700`}
                    data-testid={`selected-item-${item.id}`}
                  >
                    <FontAwesomeIcon
                      icon={faGripVertical}
                      className="h-4 w-4 text-gray-400 mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.name}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.description}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(item.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
